import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Wallet, Coins, Building, Sparkles, Briefcase, RotateCcw } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/button';

export default function AssetClassBreakdown({ assets, onChangeAssets, onReset }) {
  const [openSections, setOpenSections] = useState({
    liquidites: true,
    placements: true,
    immobilier: true,
    alternatif: true,
    professionnel: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFieldChange = (category, field, value) => {
    const num = Math.max(0, parseInt(value) || 0);
    onChangeAssets(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: num
      }
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between pb-2">
        <div>
          <h3 className="text-base font-bold text-primary">Détail & Édition des Poches d'Actifs</h3>
          <p className="text-xs text-subtle">Ajustez les montants en temps réel pour simuler l'impact immédiat sur l'allocation.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onReset} className="text-xs gap-1">
          <RotateCcw className="w-3.5 h-3.5" />
          Réinitialiser au dossier
        </Button>
      </div>

      {/* 1. Liquidités */}
      <div className="workspace-card p-5 bg-surface border border-workspace rounded-xl shadow-hp-sm">
        <button
          type="button"
          onClick={() => toggleSection('liquidites')}
          className="w-full flex items-center justify-between text-left font-bold text-primary"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-info-100 dark:bg-info-900/30 text-info-600 flex items-center justify-center">
              <Wallet className="w-4 h-4" />
            </div>
            <span>Liquidités & Épargne Monétaire</span>
          </div>
          {openSections.liquidites ? <ChevronUp className="w-4 h-4 text-subtle" /> : <ChevronDown className="w-4 h-4 text-subtle" />}
        </button>

        <AnimatePresence>
          {openSections.liquidites && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-workspace grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            >
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Comptes Courants (€)</label>
                <Input
                  type="number"
                  value={assets.liquidites.compteCourant || ''}
                  onChange={(e) => handleFieldChange('liquidites', 'compteCourant', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Livrets A / LDDS (€)</label>
                <Input
                  type="number"
                  value={assets.liquidites.livrets || ''}
                  onChange={(e) => handleFieldChange('liquidites', 'livrets', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">PEL / CEL / CAT (€)</label>
                <Input
                  type="number"
                  value={assets.liquidites.pelCel || ''}
                  onChange={(e) => handleFieldChange('liquidites', 'pelCel', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Autres liquidités (€)</label>
                <Input
                  type="number"
                  value={assets.liquidites.autres || ''}
                  onChange={(e) => handleFieldChange('liquidites', 'autres', e.target.value)}
                  placeholder="0"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 2. Placements Financiers */}
      <div className="workspace-card p-5 bg-surface border border-workspace rounded-xl shadow-hp-sm">
        <button
          type="button"
          onClick={() => toggleSection('placements')}
          className="w-full flex items-center justify-between text-left font-bold text-primary"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-success-100 dark:bg-success-900/30 text-success-600 flex items-center justify-center">
              <Coins className="w-4 h-4" />
            </div>
            <span>Placements Financiers & Marchés Cotés</span>
          </div>
          {openSections.placements ? <ChevronUp className="w-4 h-4 text-subtle" /> : <ChevronDown className="w-4 h-4 text-subtle" />}
        </button>

        <AnimatePresence>
          {openSections.placements && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-workspace grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Assurance-Vie (Fonds Euros) (€)</label>
                <Input
                  type="number"
                  value={assets.placements.assuranceVieEuros || ''}
                  onChange={(e) => handleFieldChange('placements', 'assuranceVieEuros', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Assurance-Vie (Unités de Compte) (€)</label>
                <Input
                  type="number"
                  value={assets.placements.assuranceVieUC || ''}
                  onChange={(e) => handleFieldChange('placements', 'assuranceVieUC', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">PEA (Actions Européennes) (€)</label>
                <Input
                  type="number"
                  value={assets.placements.pea || ''}
                  onChange={(e) => handleFieldChange('placements', 'pea', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Compte-Titres Ordinaire (CTO) (€)</label>
                <Input
                  type="number"
                  value={assets.placements.compteTitre || ''}
                  onChange={(e) => handleFieldChange('placements', 'compteTitre', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Épargne Salariale (PEE/PERCOL) (€)</label>
                <Input
                  type="number"
                  value={assets.placements.pee || ''}
                  onChange={(e) => handleFieldChange('placements', 'pee', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Actifs Numériques & Métaux (€)</label>
                <Input
                  type="number"
                  value={assets.placements.cryptoMetaux || ''}
                  onChange={(e) => handleFieldChange('placements', 'cryptoMetaux', e.target.value)}
                  placeholder="0"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 3. Immobilier */}
      <div className="workspace-card p-5 bg-surface border border-workspace rounded-xl shadow-hp-sm">
        <button
          type="button"
          onClick={() => toggleSection('immobilier')}
          className="w-full flex items-center justify-between text-left font-bold text-primary"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-warning-100 dark:bg-warning-900/30 text-warning-600 flex items-center justify-center">
              <Building className="w-4 h-4" />
            </div>
            <span>Immobilier, Foncier & Dettes</span>
          </div>
          {openSections.immobilier ? <ChevronUp className="w-4 h-4 text-subtle" /> : <ChevronDown className="w-4 h-4 text-subtle" />}
        </button>

        <AnimatePresence>
          {openSections.immobilier && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-workspace grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Résidence Principale (Valeur Brute) (€)</label>
                <Input
                  type="number"
                  value={assets.immobilier.residencePrincipale || ''}
                  onChange={(e) => handleFieldChange('immobilier', 'residencePrincipale', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Résidence Secondaire (€)</label>
                <Input
                  type="number"
                  value={assets.immobilier.residenceSecondaire || ''}
                  onChange={(e) => handleFieldChange('immobilier', 'residenceSecondaire', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Investissement Locatif Direct (€)</label>
                <Input
                  type="number"
                  value={assets.immobilier.locatif || ''}
                  onChange={(e) => handleFieldChange('immobilier', 'locatif', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Pierre-Papier (SCPI, OPCI) (€)</label>
                <Input
                  type="number"
                  value={assets.immobilier.scpi || ''}
                  onChange={(e) => handleFieldChange('immobilier', 'scpi', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-danger-500 mb-1">Passif Immobilier (Capital Restant Dû) (€)</label>
                <Input
                  type="number"
                  value={assets.immobilier.dettesImmo || ''}
                  onChange={(e) => handleFieldChange('immobilier', 'dettesImmo', e.target.value)}
                  placeholder="0"
                  className="border-danger-200 focus:border-danger-500"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 4. Actifs Alternatifs & Private Equity */}
      <div className="workspace-card p-5 bg-surface border border-workspace rounded-xl shadow-hp-sm">
        <button
          type="button"
          onClick={() => toggleSection('alternatif')}
          className="w-full flex items-center justify-between text-left font-bold text-primary"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <span>Alternatifs & Private Equity (Hipparchus)</span>
          </div>
          {openSections.alternatif ? <ChevronUp className="w-4 h-4 text-subtle" /> : <ChevronDown className="w-4 h-4 text-subtle" />}
        </button>

        <AnimatePresence>
          {openSections.alternatif && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-workspace grid grid-cols-1 sm:grid-cols-3 gap-4"
            >
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Transactions & Club Deals (€)</label>
                <Input
                  type="number"
                  value={assets.alternatif.participationsHipparchus || ''}
                  onChange={(e) => handleFieldChange('alternatif', 'participationsHipparchus', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Fonds de Private Equity Directs (€)</label>
                <Input
                  type="number"
                  value={assets.alternatif.privateEquity || ''}
                  onChange={(e) => handleFieldChange('alternatif', 'privateEquity', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Dette Privée & Obligations (€)</label>
                <Input
                  type="number"
                  value={assets.alternatif.dettePrivee || ''}
                  onChange={(e) => handleFieldChange('alternatif', 'dettePrivee', e.target.value)}
                  placeholder="0"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 5. Professionnel */}
      <div className="workspace-card p-5 bg-surface border border-workspace rounded-xl shadow-hp-sm">
        <button
          type="button"
          onClick={() => toggleSection('professionnel')}
          className="w-full flex items-center justify-between text-left font-bold text-primary"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center">
              <Briefcase className="w-4 h-4" />
            </div>
            <span>Patrimoine Professionnel & Entreprises</span>
          </div>
          {openSections.professionnel ? <ChevronUp className="w-4 h-4 text-subtle" /> : <ChevronDown className="w-4 h-4 text-subtle" />}
        </button>

        <AnimatePresence>
          {openSections.professionnel && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-workspace grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Valorisation Titres & Parts Sociales (€)</label>
                <Input
                  type="number"
                  value={assets.professionnel.valorisationParts || ''}
                  onChange={(e) => handleFieldChange('professionnel', 'valorisationParts', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1">Compte Courant d'Associé (CCA) (€)</label>
                <Input
                  type="number"
                  value={assets.professionnel.compteCourantAssocie || ''}
                  onChange={(e) => handleFieldChange('professionnel', 'compteCourantAssocie', e.target.value)}
                  placeholder="0"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

